import { AREAS, DISTRICTS } from '@propertypal/shared/src/constants/housePrices';
import OgImage from '@propertypal/shared/src/resources/images/price-tracker/open-graph-price-tracker.jpg';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import strapiAxios, { STRAPI_URL } from '@propertypal/shared/src/services/axios.strapi';
import { MarketTrendsResponse } from '@propertypal/shared/src/types/marketTrends';
import { StrapiResponse } from '@propertypal/shared/src/types/strapi/generic';
import { ContentHubArticle } from '@propertypal/shared/src/types/strapi/overrides';
import platform from '@propertypal/shared/src/utils/platform';
import slugify from '@propertypal/shared/src/utils/slugify';
import ContentHubCarousel from '@propertypal/web-ui/src/content-hub/ContentHubCarousel';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import MarketTrendsLinks from '@propertypal/web-ui/src/price-tracker/MarketTrendsLinks';
import Head from 'next/head';
import React, { FunctionComponent, useEffect, useState } from 'react';
import generateMetaTags from '../components/layout/MetaTags';
import MarketTrends, { getPageTitle } from '../components/price-tracker/MarketTrends';
import { requireAuthToken } from '../utils/auth';

interface Props {
  marketTrends: MarketTrendsResponse;
  marketReports: ContentHubArticle[] | null;
  councilArea: string | null;
  electoralArea: string | null;
}

export const getServerSideProps = requireAuthToken(async (ctx) => {
  let marketReports: Props['marketReports'] = null;
  let url = `${API_URL}/market-trends`;

  const councilArea = DISTRICTS.find((d) => slugify(d.name) === ctx.query.area);
  const electoralArea = AREAS.find((a) => slugify(a.name) === ctx.query.electoralArea);

  if (councilArea) url += `?councilArea=${councilArea.id}`;
  if (councilArea && electoralArea) url += `&electoralArea=${electoralArea.id}`;

  const result = await axios<MarketTrendsResponse>(
    {
      method: 'GET',
      url,
    },
    undefined,
    ctx,
  );

  const platformFilter: Record<string, string> = {
    'filters[$and][2][subCategories][$or][0][platform][$eq]':
      platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
    'filters[$and][2][subCategories][$or][1][platform][$eq]': 'Both',
    'filters[$and][3][$or][0][platform][$eq]': platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
    'filters[$and][3][$or][1][platform][$eq]': 'Both',
  };

  if (platform.title === 'PropertyPal') {
    platformFilter['filters[$and][2][subCategories][$or][2][platform][$null]'] = 'true';
    platformFilter['filters[$and][3][$or][2][platform][$null]'] = 'true';
  }

  marketReports =
    (
      await strapiAxios<StrapiResponse<ContentHubArticle[]>>({
        method: 'GET',
        url: `${STRAPI_URL}/articles`,
        params: {
          populate: 'poster,tabletPoster,mobilePoster,subCategories,content',
          'filters[$and][0][$or][0][hidden][$eq]': 'false',
          'filters[$and][0][$or][1][hidden][$null]': 'true',
          'filters[$and][1][subCategories][slug][$in]': 'market-reports',
          ...platformFilter,
          sort: 'publishedAt:desc',
          'pagination[pageSize]': 3,
          'pagination[page]': 1,
        },
      })
    )?.data?.data || null;

  return {
    props: {
      marketTrends: result.data,
      marketReports,
      councilArea: councilArea?.id || null,
      electoralArea: electoralArea?.id || null,
    },
  };
});

const NIHousePricesPage: FunctionComponent<Props> = (props) => {
  const [councilArea, setCouncilArea] = useState<string>(props.councilArea || '');
  const [electoralArea, setElectoralArea] = useState<string>(props.electoralArea || '');

  useEffect(() => {
    if (props.councilArea !== councilArea) setCouncilArea(props.councilArea || '');
    if (props.electoralArea !== electoralArea) setElectoralArea(props.electoralArea || '');
  }, [props.councilArea, props.electoralArea]);

  return (
    <>
      <Head>
        {generateMetaTags({
          title: getPageTitle(councilArea, electoralArea),
          // eslint-disable-next-line max-len
          description: `Explore PropertyPal's Northern Ireland Market Trends for up-to-date insights on house price movements and key market data across all local areas in Northern Ireland.`,
          url: '/northern-ireland-house-prices',
          image: {
            url: OgImage.src,
            width: 1200,
            height: 650,
          },
        })}
      </Head>

      <MarketTrends
        marketTrends={props.marketTrends}
        councilArea={councilArea}
        electoralArea={electoralArea}
        setCouncilArea={setCouncilArea}
        setElectoralArea={setElectoralArea}
      />

      {props.marketReports && props.marketReports?.length > 0 && (
        <ContentHubCarousel
          articles={props.marketReports}
          title="Market Reports"
          viewMore="/news-and-analysis/market-reports"
          maxWidth={MAX_WIDTH - 30}
        />
      )}

      <MarketTrendsLinks />
    </>
  );
};

export default NIHousePricesPage;
